import { useRef, useState } from "react";
import {
  Bold, Heading, List, AlignLeft, AlignCenter, AlignRight, AlignJustify, Palette, PaintBucket, Eye,
} from "lucide-react";
import { RichTextBlocks } from "@/components/RichText";
import { textareaCls } from "@/pages/admin/ui";
import LinkInserter from "@/components/admin/LinkInserter";
import ImageInserter from "@/components/admin/ImageInserter";
import ButtonInserter from "@/components/admin/ButtonInserter";
import BoxInserter from "@/components/admin/BoxInserter";

const ALIGN_MARK_RE = /^\s*::(?:center|right|justify|left|c|r|j|l)\b[ \t]?/i;

export default function RichTextEditor({ value = "", onChange, testId = "rte", rows = 8 }) {
  const taRef = useRef(null);
  const selRef = useRef({ start: 0, end: 0 });
  const [showPreview, setShowPreview] = useState(true);
  const [colorForm, setColorForm] = useState({ open: false, color: "#0052FF", size: 16 });
  const [gradForm, setGradForm] = useState({ open: false, c1: "#0052FF", c2: "#8B5CF6" });

  const syncSel = (e) => {
    selRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd };
  };
  const getSel = () => {
    const el = taRef.current;
    if (el && document.activeElement === el) return { start: el.selectionStart, end: el.selectionEnd };
    const { start, end } = selRef.current;
    return { start: Math.min(start, value.length), end: Math.min(end, value.length) };
  };
  const focusRange = (s, e) => {
    requestAnimationFrame(() => {
      const el = taRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(s, e);
      selRef.current = { start: s, end: e };
    });
  };

  const applyWrap = (before, after) => {
    const { start, end } = getSel();
    const sel = value.slice(start, end);
    const next = value.slice(0, start) + before + sel + after + value.slice(end);
    onChange(next);
    focusRange(start + before.length, start + before.length + sel.length);
  };

  const prefixLine = (prefix) => {
    const { start } = getSel();
    const ls = value.lastIndexOf("\n", start - 1) + 1;
    const next = value.slice(0, ls) + prefix + value.slice(ls);
    onChange(next);
    focusRange(start + prefix.length, start + prefix.length);
  };

  const setLineAlign = (al) => {
    const { start } = getSel();
    const ls = value.lastIndexOf("\n", start - 1) + 1;
    let le = value.indexOf("\n", ls);
    if (le === -1) le = value.length;
    let line = value.slice(ls, le).replace(ALIGN_MARK_RE, "");
    const marker = al === "left" ? "" : `::${al} `;
    onChange(value.slice(0, ls) + marker + line + value.slice(le));
    focusRange(ls + marker.length, ls + marker.length);
  };

  const insertAt = (text) => {
    const { start, end } = getSel();
    const next = value.slice(0, start) + text + value.slice(end);
    onChange(next);
    focusRange(start + text.length, start + text.length);
  };

  const insertBox = (token) => {
    const { start, end } = getSel();
    const pre = start > 0 && value[start - 1] !== "\n" ? "\n" : "";
    const post = end < value.length && value[end] !== "\n" ? "\n" : "";
    const next = value.slice(0, start) + pre + token + post + value.slice(end);
    onChange(next);
    focusRange(start + pre.length + token.length, start + pre.length + token.length);
  };

  const applyColor = () => {
    applyWrap("%[", `](|color=${colorForm.color}|size=${colorForm.size})`);
    setColorForm((f) => ({ ...f, open: false }));
  };

  const applyGradTitle = () => {
    prefixLine(`# {grad=${gradForm.c1},${gradForm.c2}} `);
    setGradForm((f) => ({ ...f, open: false }));
  };

  const ToolBtn = ({ icon: Icon, label, onClick, tid }) => (
    <button
      type="button"
      data-testid={`rte-${testId}-${tid}`}
      onClick={onClick}
      title={label}
      className="flex h-8 w-8 items-center justify-center rounded-lg border border-line bg-white text-slate-600 transition-colors hover:border-brand hover:text-brand"
    >
      <Icon className="h-4 w-4" />
    </button>
  );

  return (
    <div className="space-y-2" data-testid={`rte-${testId}`}>
      <div className="flex flex-wrap items-center gap-1.5 rounded-xl border border-line bg-surface p-2">
        <ToolBtn icon={Bold} label="Gras" tid="bold" onClick={() => applyWrap("**", "**")} />
        <ToolBtn icon={Heading} label="Titre" tid="heading" onClick={() => prefixLine("# ")} />
        <ToolBtn icon={List} label="Liste à puces" tid="list" onClick={() => prefixLine("- ")} />
        <span className="mx-0.5 h-6 w-px bg-line" />
        <ToolBtn icon={AlignLeft} label="Aligner à gauche" tid="align-left" onClick={() => setLineAlign("left")} />
        <ToolBtn icon={AlignCenter} label="Centrer" tid="align-center" onClick={() => setLineAlign("center")} />
        <ToolBtn icon={AlignRight} label="Aligner à droite" tid="align-right" onClick={() => setLineAlign("right")} />
        <ToolBtn icon={AlignJustify} label="Justifier" tid="align-justify" onClick={() => setLineAlign("justify")} />
        <span className="mx-0.5 h-6 w-px bg-line" />
        <ToolBtn icon={Palette} label="Couleur & taille du texte" tid="color" onClick={() => setColorForm((f) => ({ ...f, open: !f.open }))} />
        <ToolBtn icon={PaintBucket} label="Titre en dégradé" tid="grad-title" onClick={() => setGradForm((f) => ({ ...f, open: !f.open }))} />
        <span className="mx-0.5 h-6 w-px bg-line" />
        <ToolBtn icon={Eye} label={showPreview ? "Masquer l'aperçu" : "Afficher l'aperçu"} tid="toggle-preview" onClick={() => setShowPreview((s) => !s)} />
      </div>

      {colorForm.open && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-brand/30 bg-brand-soft/40 p-3" data-testid={`rte-${testId}-color-panel`}>
          <label className="flex items-center gap-1.5 text-[11px] font-bold text-slate-600">
            Couleur du texte
            <input type="color" data-testid={`rte-${testId}-color-value`} value={colorForm.color} onChange={(e) => setColorForm((f) => ({ ...f, color: e.target.value }))} className="h-8 w-10 cursor-pointer rounded border border-line" />
          </label>
          <label className="flex items-center gap-1.5 text-[11px] font-bold text-slate-600">
            Taille
            <input type="number" min={10} max={48} data-testid={`rte-${testId}-color-size`} value={colorForm.size} onChange={(e) => setColorForm((f) => ({ ...f, size: Number(e.target.value) }))} className="h-8 w-16 rounded-lg border border-line px-2 text-xs" />
            px
          </label>
          <button type="button" data-testid={`rte-${testId}-color-apply`} onClick={applyColor} className="rounded-lg bg-brand px-3 py-1.5 text-xs font-bold text-white">
            Appliquer au texte sélectionné
          </button>
        </div>
      )}

      {gradForm.open && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-fuchsia-300/40 bg-fuchsia-50/50 p-3" data-testid={`rte-${testId}-grad-panel`}>
          <span className="text-[11px] font-bold text-slate-600">Titre en dégradé</span>
          <label className="flex items-center gap-1.5 text-[11px] font-bold text-slate-600">
            Couleur 1
            <input type="color" data-testid={`rte-${testId}-grad-c1`} value={gradForm.c1} onChange={(e) => setGradForm((f) => ({ ...f, c1: e.target.value }))} className="h-8 w-10 cursor-pointer rounded border border-line" />
          </label>
          <label className="flex items-center gap-1.5 text-[11px] font-bold text-slate-600">
            Couleur 2
            <input type="color" data-testid={`rte-${testId}-grad-c2`} value={gradForm.c2} onChange={(e) => setGradForm((f) => ({ ...f, c2: e.target.value }))} className="h-8 w-10 cursor-pointer rounded border border-line" />
          </label>
          <span
            className="rounded-lg bg-white px-3 py-1 text-sm font-extrabold"
            style={{ backgroundImage: `linear-gradient(90deg, ${gradForm.c1}, ${gradForm.c2})`, WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent", WebkitTextFillColor: "transparent" }}
          >
            Aperçu du titre
          </span>
          <button type="button" data-testid={`rte-${testId}-grad-apply`} onClick={applyGradTitle} className="rounded-lg bg-fuchsia-600 px-3 py-1.5 text-xs font-bold text-white">
            Créer un titre en dégradé
          </button>
        </div>
      )}

      <textarea
        ref={taRef}
        data-testid={`product-form-${testId}`}
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onSelect={syncSel}
        onKeyUp={syncSel}
        onMouseUp={syncSel}
        onBlur={syncSel}
        className={textareaCls}
        placeholder={"Rédigez votre description. Utilisez la barre d'outils pour la mise en forme, l'alignement et les cadres. L'aperçu ci-dessous correspond exactement à la fiche produit."}
      />

      <div className="flex flex-wrap gap-4">
        <LinkInserter testId={testId} onInsert={insertAt} />
        <ImageInserter testId={testId} onInsert={insertAt} />
        <ButtonInserter testId={testId} onInsert={(t) => insertAt(`\n${t}\n`)} />
        <BoxInserter testId={testId} onInsert={insertBox} getSelected={() => value.slice(getSel().start, getSel().end)} />
      </div>

      {showPreview && (
        <div className="rounded-xl border border-dashed border-line bg-white p-4" data-testid={`rte-${testId}-preview`}>
          <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">Aperçu (identique à la fiche produit)</p>
          {value.trim()
            ? <RichTextBlocks text={value} className="text-sm text-slate-700" />
            : <p className="text-sm text-slate-400">L'aperçu de votre description s'affichera ici.</p>}
        </div>
      )}
    </div>
  );
}
